


















































































































import { Vue, Component, Prop } from 'vue-property-decorator'
import Decimal from 'decimal.js'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { formatDigitsGrouping } from "../../../common/utils/formattingUtils"
import { Indicator } from "../../../common/types/Indicator"
import { Kf219CoRow, Kf219CoTableData } from "../table-data/Kf219CoTableData"

type Kf219CoPart = 'contingent' | 'deductions'

/** Ключ итоговой колонки (в indicators и indicators_order) */
const TOTAL_KEY = 'total'

@Component({})
export default class ReportKf219Co extends Vue {
    @Prop({ required: true }) tableData!: Kf219CoTableData
    /** Видимость колонок из формы (ключи месяцев 'YYYY-MM' и 'total'); отсутствующие ключи считаются видимыми */
    @Prop({ default: () => [] }) propIndicators!: Indicator[]
    @Prop({ default: true }) isTree!: boolean
    @Prop({ default: false }) roundUp!: boolean
    @Prop({ default: null }) roundUpTo!: number | null

    /** Часть отчёта: по умолчанию "Контингент"; строки у частей одинаковые, меняются суммы */
    public part: Kf219CoPart = 'contingent'

    /** Параметры формата берутся на момент формирования: таблица не должна меняться от правок формы без "Сформировать" */
    public treeMode: boolean = true
    private decimals: number = 5

    public indicators: Indicator[] = []
    public expandedNodes: Record<number, boolean> = {}

    public currentPage: number = 1
    public readonly perPage: number = 10

    // ==============================================================================================
    // Инициализация
    // ==============================================================================================

    private created() {
        this.treeMode = this.isTree
        // Выключенное округление (или не заданное N) = 5 знаков, иначе до N; поле формы может вернуть строку
        const roundTo = this.roundUp && this.roundUpTo !== null && String(this.roundUpTo) !== '' ? Number(this.roundUpTo) : 5
        this.decimals = Number.isNaN(roundTo) ? 5 : roundTo

        this.indicators = this.columnKeys.map(key => ({
            key,
            active: this.propIndicators.find(it => it.key === key)?.active ?? true,
        }))
        this.emitIndicators()

        // После формирования дерево раскрыто до категорий
        const expanded: Record<number, boolean> = {}
        for (const row of this.rows) {
            if (row.hasChild && row.tree_level < 1) {
                expanded[row.idx] = true
            }
        }
        this.expandedNodes = expanded
    }

    /** Метод-обертка для $t; ключи с "../" берутся из общего словаря конструктора */
    public tr(key: string): string {
        const base = 'modules.budget_execution_monitoring.reports_constructor'
        if (key.startsWith('../')) {
            return String(this.$t(`${base}.${key.substring(3)}`))
        }
        return String(this.$t(`${base}.kf219_co.table.${key}`))
    }

    // ==============================================================================================
    // Данные
    // ==============================================================================================

    private get rows(): Kf219CoRow[] {
        return this.tableData?.rows ?? []
    }

    private get totalRow(): Kf219CoRow | null {
        return this.rows.find(row => row.row_level === 0) ?? null
    }

    /** Строки без "Итого" */
    private get dataRows(): Kf219CoRow[] {
        return this.rows.filter(row => row.row_level !== 0)
    }

    public get hasDataRows(): boolean {
        return this.dataRows.length > 0
    }

    public get codeColumns(): string[] {
        return this.tableData?.code_columns ?? []
    }

    /** Ключи всех колонок с суммами: месяцы периода и "Итого" */
    private get columnKeys(): string[] {
        return [...(this.tableData?.months ?? []), TOTAL_KEY]
    }

    public get visibleIndicatorKeys(): string[] {
        return this.indicators.filter(it => it.active).map(it => it.key)
    }

    /** Подпись колонки: "Январь 2026" (название месяца — из словаря конструктора) или "Итого" */
    public columnLabel(key: string): string {
        if (key === TOTAL_KEY) {
            return this.tr('../total')
        }
        const [year, month] = key.split('-')
        const monthName = this.tr('../month' + Number(month))
        return `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${year}`
    }

    // ==============================================================================================
    // Предупреждение о дате данных
    // ==============================================================================================

    public get dataAsOfWarning(): string | null {
        const dataAsOf = this.tableData?.data_as_of
        const periodTo = this.tableData?.period_to
        if (!dataAsOf || !periodTo || dataAsOf >= periodTo) {
            return null
        }
        return this.tr('data_as_of_warning').replace('$date', moment(dataAsOf, 'YYYY-MM-DD').format('DD.MM.YYYY'))
    }

    // ==============================================================================================
    // Переключатель частей
    // ==============================================================================================

    public get partOptions(): { value: Kf219CoPart, text: string }[] {
        return [
            { value: 'contingent', text: this.tr('part_contingent') },
            { value: 'deductions', text: this.tr('part_deductions') },
        ]
    }

    // ==============================================================================================
    // Скрытие показателей (действует на обе части)
    // ==============================================================================================

    /** Последнюю видимую колонку скрыть нельзя */
    public isLastActiveIndicator(key: string): boolean {
        const indicator = this.indicators.find(it => it.key === key)
        if (!indicator || !indicator.active) return false
        return this.indicators.filter(it => it.active).length <= 1
    }

    public onIndicatorChange() {
        this.emitIndicators()
    }

    /** Синхронизирует видимость колонок с формой и параметрами выгрузки в журнал (indicators_order) */
    private emitIndicators() {
        this.$emit('update-indicators', cloneDeep(this.indicators))
    }

    // ==============================================================================================
    // Дерево
    // ==============================================================================================

    public isExpanded(idx: number): boolean {
        return !!this.expandedNodes[idx]
    }

    public toggleNode(idx: number): void {
        this.$set(this.expandedNodes, idx, !this.isExpanded(idx))
    }

    private get expandableRows(): Kf219CoRow[] {
        return this.rows.filter(row => row.hasChild)
    }

    public get hasAnyExpandable(): boolean {
        return this.expandableRows.length > 0
    }

    public get allExpanded(): boolean {
        return this.hasAnyExpandable && this.expandableRows.every(row => this.isExpanded(row.idx))
    }

    public toggleAll(): void {
        const expand = !this.allExpanded
        const expanded: Record<number, boolean> = {}
        for (const row of this.expandableRows) {
            expanded[row.idx] = expand
        }
        this.expandedNodes = expanded
    }

    /** Плоский список (без дерева): всё, кроме "Итого", которое закреплено сверху */
    public get flatRows(): Kf219CoRow[] {
        return this.dataRows
    }

    /** Видимые строки: в дереве — те, у кого раскрыта вся цепочка родителей; без дерева — "Итого" и страница плоского списка */
    public get visibleRows(): Kf219CoRow[] {
        if (!this.treeMode) {
            const start = (this.currentPage - 1) * this.perPage
            const page = this.flatRows.slice(start, start + this.perPage)
            return this.totalRow ? [this.totalRow, ...page] : page
        }

        // Родители идут раньше детей (порядок вывода), поэтому достаточно одного прохода
        const shown = new Set<number>()
        const result: Kf219CoRow[] = []
        for (const row of this.rows) {
            const parent = row.parent_idx
            if (parent === null || parent === undefined || (shown.has(parent) && this.isExpanded(parent))) {
                shown.add(row.idx)
                result.push(row)
            }
        }
        return result
    }

    public nameIndent(row: Kf219CoRow): string {
        if (!this.treeMode) return '0.75rem'
        return (0.75 + Math.max(row.tree_level - 1, 0) * 1.25) + 'rem'
    }

    // ==============================================================================================
    // Значения ячеек
    // ==============================================================================================

    /** Значение ячейки выбранной части; null — "—" */
    public cellValue(row: Kf219CoRow, key: string): number | null {
        if (key === TOTAL_KEY) {
            return this.part === 'contingent' ? row.contingent_total : row.deductions_total
        }
        const index = (this.tableData?.months ?? []).indexOf(key)
        if (index < 0) return null
        const values = this.part === 'contingent' ? row.contingent : row.deductions
        return values?.[index] ?? null
    }

    /** Округление на клиенте, разделитель тысяч — пробел, хвостовые нули обрезаются */
    public formatCell(value: number | null): string {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '—'
        }
        let text = new Decimal(value).toFixed(this.decimals, Decimal.ROUND_HALF_UP)
        if (text.includes('.')) {
            text = text.replace(/0+$/, '').replace(/\.$/, '')
        }
        if (text === '-0') {
            text = '0'
        }
        return formatDigitsGrouping(text)
    }
}
